import React, { useState } from 'react';
import {
  Wrench,
  Clock,
  Calendar,
  ChevronRight,
  PlusCircle,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import type { Machine } from '../../types';
import {
  evaluateMachine,
  type MachineScheduleResult,
  formatHours,
} from '../../services/scheduler';
import { StatusBadge } from './StatusBadge';
import { HoursTracker } from './HoursTracker';
import { ServiceForecast } from './ServiceForecast';
import { Button } from '../common/Button';
import { Card } from '../common/Card';

export interface MachineCardProps {
  machine: Machine;
  isSelected?: boolean;
  onSelect?: (machineId: string) => void;
  onStartInspection?: (machineId: string) => void;
  onAddHours?: (machineId: string, currentHours: number) => void;
  className?: string;
}

export function MachineCard({
  machine,
  isSelected = false,
  onSelect,
  onStartInspection,
  onAddHours,
  className = '',
}: MachineCardProps) {
  const [showForecast, setShowForecast] = useState<boolean>(false);

  // Evaluate machine status mathematically
  const scheduleResult: MachineScheduleResult = evaluateMachine(machine);
  const status = scheduleResult.overallStatus;

  const statusBorder =
    status === 'OVERDUE' ? 'overdue' : status === 'DUE_SOON' ? 'due' : 'normal';

  // Format last inspection date
  const lastDateFormatted = machine.lastInspectionDate
    ? new Date(machine.lastInspectionDate).toLocaleString('th-TH', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : 'ยังไม่มีประวัติการตรวจเช็ก';

  return (
    <Card
      statusBorder={statusBorder}
      className={`transition-all duration-200 cursor-pointer ${
        isSelected
          ? 'ring-2 ring-blue-500 shadow-md bg-blue-50/40'
          : 'hover:border-slate-300 bg-white shadow-xs'
      } ${className}`}
      onClick={() => onSelect?.(machine.id)}
      title={
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-bold text-base sm:text-lg text-slate-900">
              {machine.name}
            </span>
            <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-blue-700 font-semibold border border-slate-200">
              {machine.id}
            </span>
          </div>
          <StatusBadge status={status} size="sm" />
        </div>
      }
      subtitle={
        <span className="text-xs text-slate-500">
          Model: <strong className="text-slate-800">{machine.model}</strong>
          {machine.serialNo && ` | S/N: ${machine.serialNo}`}
        </span>
      }
      footer={
        <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowForecast(!showForecast);
            }}
            className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 min-h-[36px] px-2 py-1 rounded hover:bg-blue-50 transition-colors"
          >
            <TrendingUp className="w-3.5 h-3.5" />
            {showForecast ? 'ซ่อนแผนพยากรณ์' : 'ดูแผนพยากรณ์ (500h..10000h)'}
          </button>

          <div className="flex items-center gap-2">
            {onAddHours && (
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddHours(machine.id, machine.cumulativeHours);
                }}
                leftIcon={<PlusCircle className="w-3.5 h-3.5" />}
              >
                + ชั่วโมง
              </Button>
            )}

            {onStartInspection && (
              <Button
                size="sm"
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onStartInspection(machine.id);
                }}
                rightIcon={<ChevronRight className="w-4 h-4" />}
              >
                เริ่มตรวจ PM
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {/* Worst-Case Status Alert Line if not normal */}
        {status !== 'NORMAL' && (
          <div
            className={`p-2.5 rounded-lg text-xs font-medium border flex items-start gap-2 ${
              status === 'OVERDUE'
                ? 'bg-rose-50 border-rose-200 text-rose-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{scheduleResult.worstCaseReason}</p>
            </div>
          </div>
        )}

        {/* Operating Hours Tracker Component */}
        <HoursTracker
          cumulativeHours={machine.cumulativeHours}
          lastServiceHours={machine.lastInspectionHours || 0}
          onAddHoursClick={
            onAddHours ? () => onAddHours(machine.id, machine.cumulativeHours) : undefined
          }
        />

        {/* Inspection History Timestamp */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            ตรวจเช็กล่าสุด:
          </span>
          <span className="font-medium text-slate-800">{lastDateFormatted}</span>
        </div>

        {/* Optional Collapsible Service Forecast */}
        {showForecast && (
          <div className="pt-2 animate-in fade-in duration-200">
            <ServiceForecast
              cumulativeHours={machine.cumulativeHours}
              lastServiceHours={machine.lastInspectionHours || 0}
              initiallyExpanded={true}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
